/* eslint-disable @typescript-eslint/ban-ts-comment */
import React from "react";
import { fabric } from "fabric";
import { Thunk, actions, Action, State } from "../module";
import { FabricObject, RegionData } from "../types";
import {
  configureCanvasOptions,
  getCanvasDimensions,
  handleMoveLimit,
  handleRotateLimit,
  handleScalingLimit,
  handleTextBoxChange,
} from "./utils";
import selectors from "../selectors";
import { TEXT_BOX_PLACEHOLDER_TEXT } from "../CustomizableTool/constants";
import { FabricTypes } from "./constants";

// @ts-ignore
const init: Thunk =
  (state: State, dispatch: React.Dispatch<Action>) =>
  (
    region: RegionData,
    newCanvas: fabric.Canvas,
    onLoad?: (previousRegionId: number, newCanvas: fabric.Canvas) => void,
    onSelectionChange?: (
      obj: fabric.Object | null,
      isSelected: boolean,
    ) => void,
  ) => {
    const previousRegionId = state.fabric.activeRegion?.id;
    if (region.id) {
      dispatch({
        type: actions.fabric.fabricRegionChanged,
        payload: { region },
      });
    }

    newCanvas.setDimensions(getCanvasDimensions(region));

    newCanvas.loadFromJSON(state.fabric.regions[region.id], () => null);

    // Add Dashed Outline of Region
    const { mapping } = region;

    // Set additional props to be used in 3d rendering logic later
    // @ts-ignore

    newCanvas.mapping = mapping;

    // generateCanvasGridLines(newCanvas);

    if (onSelectionChange) {
      newCanvas.on("selection:updated", (event) => {
        onSelectionChange(event!.selected![0]!, true);
      });
      newCanvas.on("selection:created", (event) => {
        onSelectionChange(event!.selected![0]!, true);
      });

      // Clear selection on region/canvas change
      onSelectionChange(null, false);
    }

    // Ensures that buttons render when selecting an object
    newCanvas.on("selection:cleared", (event) => {
      const target = event.deselected?.[0];

      // @ts-ignore
      if (target?.type === FabricTypes.textBox && !target?.text?.length) {
        target
          ?.set({
            // @ts-ignore
            isPlaceholder: true,
            text: TEXT_BOX_PLACEHOLDER_TEXT,
          })
          .setCoords();
      }

      if (onSelectionChange && target) {
        onSelectionChange(target, false);
      }

      newCanvas.renderAll();
    });

    newCanvas.on("text:editing:entered", ({ target }) => {
      // Clear our placeholder text before we begin typing

      // @ts-ignore
      if (target.isPlaceholder) {
        target?.set({
          // @ts-ignore
          isPlaceholder: false,
          text: "",
          dirty: true,
        });

        // @ts-ignore

        target.hiddenTextarea.value = "";
        target?.setCoords();
      }
    });

    newCanvas.on("text:editing:exited", ({ target }) => {
      // Clear our placeholder text before we begin typing

      // @ts-ignore
      if (!target.text.trim().length) {
        target?.set({
          // @ts-ignore
          isPlaceholder: true,
          text: TEXT_BOX_PLACEHOLDER_TEXT,
          dirty: true,
        });

        target?.setCoords();
      }
    });

    newCanvas.on("text:changed", ({ target }) => {
      if (target) {
        handleTextBoxChange(
          // @ts-ignore
          target,
          selectors.getCurrentRegionSelectedSpecialOptions(state, dispatch)(),
        );

        // Setting isPlaceholder = false while typing allows the user to
        // align their textbox before they commit their text
        // @ts-ignore
        if (target.text !== TEXT_BOX_PLACEHOLDER_TEXT) {
          // @ts-ignore

          target.isPlaceholder = false;

          if (onSelectionChange) {
            onSelectionChange(target, true);
          }
        }
      }
    });

    newCanvas.on("object:moving", ({ target }) => {
      if (target) {
        handleMoveLimit(target);
      }
    });

    newCanvas.on("object:rotating", ({ target }) => {
      const fabricObject = target as FabricObject;
      if (target) {
        handleRotateLimit(fabricObject);
      }
    });

    newCanvas.on("object:scaling", ({ target }) => {
      if (target) {
        const fabricObject = target as FabricObject;
        handleScalingLimit(fabricObject);
      }
    });

    // This is temporary to show the bounding box during development - remove before sending to dev.
    // addDebugging(newCanvas);

    configureCanvasOptions(newCanvas);
    newCanvas.requestRenderAll();

    if (onLoad && previousRegionId) {
      onLoad(previousRegionId, newCanvas);
    }

    dispatch({ type: actions.fabric.initialized, payload: newCanvas });
  };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const updateCanvas: Thunk = (state, dispatch) => (payload: any) =>
  dispatch({ type: actions.fabric.updated, payload });

export default {
  init,
  updateCanvas,
};
